import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { MailSettings } from '../types';

const MailSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testEmail, setTestEmail] = useState('');

  const [form, setForm] = useState<MailSettings>({
    smtpHost: 'mail.mombasa.go.ke',
    smtpPort: 465,
    smtpSecure: true,
    smtpUsername: 'pmfs@mombasa.go.ke',
    smtpPassword: '',
    incomingServer: 'mail.mombasa.go.ke',
    imapPort: 993,
    pop3Port: 995,
    fromEmail: 'pmfs@mombasa.go.ke',
    fromName: 'PMS System',
  });

  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    const loadSettings = async () => {
      if (!isAdmin) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const res = await apiService.getMailSettings();
        if (res.success && res.data) {
          setForm((prev) => ({
            ...prev,
            ...res.data,
            smtpPassword: ''
          }));
          setTestEmail(res.data.smtpUsername || user?.email || '');
        } else {
          setTestEmail(user?.email || '');
        }
      } catch (err: unknown) {
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(message || 'Failed to load mail settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [isAdmin, user?.email]);

  const updateField = <K extends keyof MailSettings>(field: K, value: MailSettings[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.smtpHost || !form.smtpPort || !form.smtpUsername || !form.incomingServer) {
      setError('Outgoing server, SMTP port, username, and incoming server are required');
      return;
    }

    setSaving(true);
    try {
      const payload: MailSettings = {
        smtpHost: form.smtpHost,
        smtpPort: Number(form.smtpPort),
        smtpSecure: Boolean(form.smtpSecure),
        smtpUsername: form.smtpUsername,
        smtpPassword: form.smtpPassword,
        incomingServer: form.incomingServer,
        imapPort: Number(form.imapPort || 993),
        pop3Port: Number(form.pop3Port || 995),
        fromEmail: form.fromEmail || form.smtpUsername,
        fromName: form.fromName || 'PMS System',
      };

      const res = await apiService.updateMailSettings(payload);
      if (res.success) {
        setSuccess('Mail settings saved successfully');
        setForm((prev) => ({
          ...prev,
          ...res.data,
          smtpPassword: ''
        }));
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
      setError(message?.error || message?.message || 'Failed to save mail settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setError('');
    setSuccess('');

    if (!testEmail.trim()) {
      setError('Enter a test recipient email');
      return;
    }

    setTesting(true);
    try {
      const res = await apiService.sendMailTest(testEmail.trim());
      if (res.success) {
        setSuccess(res.message || 'Test email sent successfully');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
      setError(message?.error || message?.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Only admins can access mail settings.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Mail Client Manual Settings
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Secure SSL/TLS Settings (Recommended)
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSave}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={form.smtpUsername || ''}
                    onChange={(e) => updateField('smtpUsername', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={form.smtpPassword || ''}
                    onChange={(e) => updateField('smtpPassword', e.target.value)}
                    placeholder="Use the email account's password"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Incoming Server"
                    value={form.incomingServer || ''}
                    onChange={(e) => updateField('incomingServer', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="IMAP Port"
                    type="number"
                    value={form.imapPort || 993}
                    onChange={(e) => updateField('imapPort', Number(e.target.value))}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="POP3 Port"
                    type="number"
                    value={form.pop3Port || 995}
                    onChange={(e) => updateField('pop3Port', Number(e.target.value))}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Outgoing Server"
                    value={form.smtpHost || ''}
                    onChange={(e) => updateField('smtpHost', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="SMTP Port"
                    type="number"
                    value={form.smtpPort || 465}
                    onChange={(e) => updateField('smtpPort', Number(e.target.value))}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="From Email"
                    value={form.fromEmail || ''}
                    onChange={(e) => updateField('fromEmail', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="From Name"
                    value={form.fromName || ''}
                    onChange={(e) => updateField('fromName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.smtpSecure)}
                        onChange={(e) => updateField('smtpSecure', e.target.checked)}
                      />
                    }
                    label="Use SSL/TLS (Secure)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button type="submit" variant="contained" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    Send Test Email
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Test Recipient Email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleSendTest}
                    disabled={testing}
                    sx={{ height: '56px' }}
                  >
                    {testing ? 'Sending...' : 'Send Test'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default MailSettingsPage;